// Illustrative product UI. Fixed design sizes (see SCREEN_SIZE) are scaled
// by <Fit> or by the hero's projective transform, so everything here is laid
// out in absolute design pixels.
import { Fragment } from 'react';
import './Screens.css';

export const SCREEN_SIZE = { w: 1280, h: 800 };
export const COMPACT_SIZE = { w: 380, h: 630 };

const ico = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

const RailIcons = {
  brief: <path d="M7 3.5h7l4 4V20a.5.5 0 0 1-.5.5h-10A.5.5 0 0 1 7 20V3.5ZM14 3.5V8h4M10 12h5M10 15.5h5" />,
  map: <path d="m3.5 6.5 5.5-2 6 2 5.5-2v13l-5.5 2-6-2-5.5 2v-13ZM9 4.5v13M15 6.5v13" />,
  wx: <path d="M7 18.5a4 4 0 0 1-.6-8 5.5 5.5 0 0 1 10.7 1.2A3.4 3.4 0 0 1 17 18.5H7Z" />,
  notam: <path d="M12 4 3.5 19h17L12 4ZM12 10v4M12 16.8v.2" />,
  wb: <path d="M12 4v16M5 20h14M6 8h12M6 8l-2.5 6h5L6 8ZM18 8l-2.5 6h5L18 8Z" />,
  log: <path d="M6 4h11a1 1 0 0 1 1 1v15H7a1 1 0 0 1-1-1V4ZM6 17h12M10 8h5" />,
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M5.6 18.4l1.8-1.8M16.6 7.4l1.8-1.8" />
    </>
  ),
};

function StatusBar({ time = '18:56Z' }) {
  return (
    <div className="scr-status">
      <span className="mono">{time}</span>
      <span className="scr-status__icons" aria-hidden="true">
        <svg width="18" height="12" viewBox="0 0 18 12" fill="currentColor">
          <rect x="0" y="8" width="3" height="4" rx="1" />
          <rect x="5" y="5.5" width="3" height="6.5" rx="1" />
          <rect x="10" y="3" width="3" height="9" rx="1" />
          <rect x="15" y="0" width="3" height="12" rx="1" opacity=".35" />
        </svg>
        <svg width="26" height="12" viewBox="0 0 26 12" fill="none" stroke="currentColor">
          <rect x="0.5" y="0.5" width="22" height="11" rx="3" opacity=".5" />
          <rect x="2.5" y="2.5" width="15" height="7" rx="1.5" fill="currentColor" stroke="none" />
          <path d="M24.5 4v4" strokeLinecap="round" opacity=".5" />
        </svg>
      </span>
    </div>
  );
}

function Cat({ kind = 'vfr' }) {
  return <span className={`scr-cat scr-cat--${kind}`}>{kind.toUpperCase()}</span>;
}

function Rail({ active }) {
  return (
    <nav className="brief__rail" aria-hidden="true">
      {['brief', 'map', 'wx', 'notam', 'wb', 'log'].map((k) => (
        <span key={k} className={`brief__rail-btn${k === active ? ' is-active' : ''}`}>
          <svg {...ico}>{RailIcons[k]}</svg>
        </span>
      ))}
      <span className="brief__rail-btn brief__rail-btn--end">
        <svg {...ico}>{RailIcons.gear}</svg>
      </span>
    </nav>
  );
}

function Tabs({ active }) {
  return (
    <div className="brief__tabs">
      {['Overview', 'Weather', 'NOTAMs', 'W&B'].map((t) => (
        <span key={t} className={t === active ? 'is-active' : undefined}>
          {t}
        </span>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- Route map */

// The route sits in the middle band of the viewBox so it survives being
// cropped ("slice") to both the tall full-size map and the wide compact one.
export function RouteMap({ className = '' }) {
  const fixes = [
    { id: 'KSFO', x: 84, y: 352, kind: 'apt', dx: -18, dy: 34 },
    { id: 'ECA', x: 196, y: 312, kind: 'vor', dx: -10, dy: 30 },
    { id: 'OAL', x: 384, y: 196, kind: 'vor', dx: -14, dy: -18 },
    { id: 'BTY', x: 512, y: 262, kind: 'vor', dx: 14, dy: -12 },
    { id: 'KLAS', x: 604, y: 316, kind: 'apt', dx: -20, dy: 34 },
  ];
  const route = 'M84 352 C124 338 160 324 196 312 S330 212 384 196 S470 238 512 262 S576 306 604 316';
  return (
    <svg className={`route-map ${className}`} viewBox="0 0 680 560" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        <radialGradient id="rm-cell" cx="50%" cy="50%" r="50%">
          <stop offset="0" stopColor="#f5d33d" stopOpacity=".55" />
          <stop offset=".35" stopColor="#3ddc84" stopOpacity=".35" />
          <stop offset="1" stopColor="#3ddc84" stopOpacity="0" />
        </radialGradient>
        <pattern id="rm-grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M40 0H0V40" fill="none" stroke="rgba(255,255,255,.045)" />
        </pattern>
        <filter id="rm-glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
      </defs>
      <rect width="680" height="560" fill="url(#rm-grid)" />
      <path d="M0 0H92C66 90 96 170 62 260S38 420 70 560H0Z" fill="rgba(76,141,255,.08)" />
      <path d="M92 0C66 90 96 170 62 260S38 420 70 560" fill="none" stroke="rgba(76,141,255,.35)" strokeWidth="1.2" />
      <g fill="none" stroke="rgba(255,255,255,.06)" strokeWidth="1.2">
        <path d="M200 0c40 96 10 190 70 290s40 180 30 270" />
        <path d="M232 0c34 92 8 184 66 284s44 186 34 276" />
        <path d="M268 0c30 86 10 180 60 276s50 190 42 284" />
        <path d="M470 0c-30 120 40 200 10 320s30 160 20 240" />
        <path d="M600 0c-20 110 30 210 0 310s20 160 30 250" />
      </g>
      <ellipse cx="452" cy="404" rx="72" ry="46" fill="url(#rm-cell)" />
      <ellipse cx="504" cy="428" rx="40" ry="26" fill="url(#rm-cell)" />
      <ellipse cx="236" cy="150" rx="46" ry="30" fill="url(#rm-cell)" opacity=".7" />
      <path d={route} fill="none" stroke="#e05cf5" strokeWidth="9" opacity=".35" filter="url(#rm-glow)" />
      <path d={route} fill="none" stroke="#e05cf5" strokeWidth="3.2" strokeLinecap="round" />
      <g fontSize="13" fill="rgba(238,240,246,.78)">
        {fixes.map((f) => (
          <g key={f.id}>
            {f.kind === 'apt' ? (
              <g stroke="#4cd3f5" strokeWidth="2" fill="#0b0c13">
                <circle cx={f.x} cy={f.y} r="8" />
                <path d={`M${f.x} ${f.y - 13}v4M${f.x} ${f.y + 9}v4M${f.x - 13} ${f.y}h4M${f.x + 9} ${f.y}h4`} />
              </g>
            ) : (
              <path
                d={`M${f.x - 7} ${f.y}l3.5-6h7l3.5 6-3.5 6h-7Z`}
                fill="#0b0c13"
                stroke="rgba(238,240,246,.8)"
                strokeWidth="1.6"
              />
            )}
            <text x={f.x + f.dx} y={f.y + f.dy}>
              {f.id}
            </text>
          </g>
        ))}
        <text x="236" y="276" fill="#e05cf5" fontSize="11">TOC</text>
        <text x="452" y="206" fill="#e05cf5" fontSize="11">TOD</text>
      </g>
      <g transform="translate(272 262) rotate(-33)">
        <path d="M14 0 4 -3V-11L1 -12V-3L-9 -1-12-6h-2l1 6-1 6h2l3-5L1 3v9l3-1V3l10-3Z" fill="#fff" />
      </g>
      <g transform="translate(300 196)">
        <rect x="-30" y="-13" width="60" height="24" rx="12" fill="rgba(11,12,19,.85)" stroke="rgba(224,92,245,.6)" />
        <text x="0" y="4" textAnchor="middle" fontSize="12" fill="#f3c6fb">FL410</text>
      </g>
      <g transform="translate(28 524)" fontSize="11" fill="rgba(238,240,246,.5)">
        <path d="M0 0h80M0 -4v8M80 -4v8" stroke="rgba(238,240,246,.5)" />
        <text x="90" y="4">50 NM</text>
      </g>
      <g transform="translate(646 46)" fontSize="11" fill="rgba(238,240,246,.6)" textAnchor="middle">
        <path d="M0 -14 6 4 0 0-6 4Z" fill="rgba(238,240,246,.7)" />
        <text y="20">N</text>
      </g>
    </svg>
  );
}

/* ---------------------------------------------------------- EFB briefing */

const STATIONS = [
  {
    id: 'KSFO',
    name: 'San Francisco Intl',
    raw: 'KSFO 011856Z 28012KT 10SM FEW015 18/12 A3002',
    rows: [
      ['Wind', '280° · 12 kt'],
      ['Visibility', '10 SM'],
      ['Clouds', 'FEW 1,500′'],
      ['Temp / dew', '18° / 12°'],
    ],
  },
  {
    id: 'KLAS',
    name: 'Harry Reid Intl',
    raw: 'KLAS 011856Z 23008KT 10SM SKC 31/M02 A2996',
    rows: [
      ['Wind', '230° · 8 kt'],
      ['Visibility', '10 SM'],
      ['Clouds', 'Clear'],
      ['Temp / dew', '31° / −2°'],
    ],
  },
];

function Station({ s, taf = false }) {
  return (
    <div className="stn">
      <div className="stn__head">
        <b>{s.id}</b>
        <Cat kind="vfr" />
        <span className="stn__time mono">18:56Z</span>
      </div>
      <div className="stn__name">{s.name}</div>
      <div className="stn__raw mono">{s.raw}</div>
      <dl className="stn__grid">
        {s.rows.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      {taf && (
        <div className="stn__taf">
          <span className="mono">TAF</span>
          <span className="stn__bars">
            <i className="is-vfr" style={{ flex: 5 }} />
            <i className="is-vfr" style={{ flex: 3 }} />
            <i className="is-mvfr" style={{ flex: 1.4 }} />
            <i className="is-vfr" style={{ flex: 4 }} />
          </span>
          <span className="mono stn__taf-end">+24h</span>
        </div>
      )}
    </div>
  );
}

const STATS = [
  ['Flight time', '1h 18m'],
  ['Planned fuel', '3,450 lb'],
  ['Cruise', 'FL410'],
  ['Weight & balance', 'Within limits', 'ok'],
  ['NOTAMs', '3 active', 'warn'],
];

function BriefingFull() {
  return (
    <div className="scr scr-brief">
      <StatusBar />
      <div className="brief__body">
        <Rail active="brief" />
        <div className="brief__main">
          <header className="brief__head">
            <div>
              <div className="brief__eyebrow mono">Preflight briefing · N250AQ · C25C</div>
              <div className="brief__route">
                <span>KSFO</span>
                <svg width="28" height="16" viewBox="0 0 28 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M2 8h22M18 2l6 6-6 6" />
                </svg>
                <span>KLAS</span>
              </div>
            </div>
            <Tabs active="Overview" />
            <div className="brief__chips">
              <span className="scr-chip scr-chip--ok">VFR on route</span>
              <span className="scr-chip">Filed · ETD 19:30Z</span>
            </div>
          </header>
          <div className="brief__grid">
            <div className="scr-card brief__map">
              <div className="brief__map-label mono">Route · 414 NM</div>
              <RouteMap />
            </div>
            <div className="scr-card brief__wx">
              <Station s={STATIONS[0]} />
              <Station s={STATIONS[1]} taf />
            </div>
          </div>
          <div className="brief__stats">
            {STATS.map(([label, value, tone]) => (
              <div key={label} className={`scr-card brief__stat${tone ? ` is-${tone}` : ''}`}>
                <span className="brief__stat-label">{label}</span>
                <span className="brief__stat-value">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function BriefingCompact() {
  return (
    <div className="scr scr-brief scr-brief--compact">
      <StatusBar />
      <header className="brief__head">
        <div className="brief__eyebrow mono">Preflight briefing · N250AQ</div>
        <div className="brief__route">
          <span>KSFO</span>
          <svg width="22" height="14" viewBox="0 0 28 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M2 8h22M18 2l6 6-6 6" />
          </svg>
          <span>KLAS</span>
          <span className="scr-chip scr-chip--ok">VFR</span>
        </div>
      </header>
      <div className="scr-card brief__map">
        <RouteMap />
      </div>
      <div className="scr-card">
        <Station s={STATIONS[0]} />
      </div>
      <div className="brief__stats">
        {STATS.slice(0, 4).map(([label, value, tone]) => (
          <div key={label} className={`scr-card brief__stat${tone ? ` is-${tone}` : ''}`}>
            <span className="brief__stat-label">{label}</span>
            <span className="brief__stat-value">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BriefingScreen({ compact = false }) {
  return compact ? <BriefingCompact /> : <BriefingFull />;
}

/* ------------------------------------------------------ Weight & balance */

const LOAD = [
  ['Basic empty weight', '10,280', false],
  ['Pilot', '190', true],
  ['Copilot', '175', true],
  ['Passengers · 4', '720', true],
  ['Nose baggage', '60', true],
  ['Aft baggage', '180', true],
  ['Fuel at ramp', '3,550', true],
];

const WB_STATS = [
  ['Zero fuel weight', '11,605 lb'],
  ['Takeoff weight', '15,055 lb'],
  ['Landing weight', '12,905 lb'],
  ['CG at takeoff', '300.6 in'],
  ['Takeoff margin', '2,055 lb', 'ok'],
];

// CG envelope: x = CG 296–306 in (48 px/in), y = weight 10k–17.5k lb.
function Envelope() {
  const points = [
    ['TOW', 281, 157],
    ['LDW', 247, 269],
    ['ZFW', 214, 336],
  ];
  return (
    <svg className="wb__envelope" viewBox="0 0 580 476" aria-hidden="true">
      <g stroke="rgba(255,255,255,.06)">
        {[60, 156, 252, 348, 444, 540].map((x) => (
          <line key={x} x1={x} x2={x} y1="30" y2="420" />
        ))}
        {[420, 316, 212, 108].map((y) => (
          <line key={y} x1="60" x2="540" y1={y} y2={y} />
        ))}
      </g>
      <g className="wb__axis">
        {[296, 298, 300, 302, 304, 306].map((v, i) => (
          <text key={v} x={60 + i * 96} y="444" textAnchor="middle">
            {v}
          </text>
        ))}
        {[
          ['10k', 420],
          ['12k', 316],
          ['14k', 212],
          ['16k', 108],
        ].map(([label, y]) => (
          <text key={label} x="48" y={y + 4} textAnchor="end">
            {label}
          </text>
        ))}
        <text x="300" y="470" textAnchor="middle">
          CG · inches aft of datum
        </text>
      </g>
      <polygon className="wb__area" points="132,420 132,238 204,50 473,50 511,160 511,420" />
      <g stroke="rgba(255,255,255,.3)" strokeDasharray="4 6" strokeWidth="1.4">
        <line x1="132" x2="511" y1="126" y2="126" />
        <line x1="132" x2="511" y1="290" y2="290" />
      </g>
      <g className="wb__axis">
        <text x="502" y="118" textAnchor="end">
          MLW 15,660
        </text>
        <text x="502" y="282" textAnchor="end">
          MZFW 12,500
        </text>
        <text x="466" y="42" textAnchor="end">
          MTOW 17,110
        </text>
      </g>
      <path d="M281 157 247 269" stroke="#fff" strokeWidth="2" strokeDasharray="2 6" strokeLinecap="round" opacity=".7" />
      {points.map(([label, x, y]) => (
        <g key={label} transform={`translate(${x} ${y})`}>
          <circle r="7" fill="#0b0c13" stroke="#7cf0b0" strokeWidth="2.5" />
          <text className="wb__point" x="14" y="5">
            {label}
          </text>
        </g>
      ))}
    </svg>
  );
}

export function WeightBalanceScreen() {
  return (
    <div className="scr scr-brief">
      <StatusBar />
      <div className="brief__body">
        <Rail active="wb" />
        <div className="brief__main">
          <header className="brief__head">
            <div>
              <div className="brief__eyebrow mono">Weight &amp; balance · N250AQ · C25C</div>
              <div className="brief__route">Loading</div>
            </div>
            <Tabs active="W&B" />
            <div className="brief__chips">
              <span className="scr-chip scr-chip--ok">Within limits</span>
            </div>
          </header>
          <div className="wb__grid">
            <div className="scr-card wb__load">
              {LOAD.map(([label, weight, adjustable]) => (
                <div key={label} className="wb__row">
                  <span>{label}</span>
                  <span className="wb__value">
                    {adjustable && <i aria-hidden="true">−</i>}
                    <b className="mono">{weight}</b>
                    {adjustable && <i aria-hidden="true">+</i>}
                  </span>
                  <span className="wb__unit">lb</span>
                </div>
              ))}
            </div>
            <div className="scr-card wb__chart">
              <Envelope />
            </div>
          </div>
          <div className="brief__stats">
            {WB_STATS.map(([label, value, tone]) => (
              <div key={label} className={`scr-card brief__stat${tone ? ` is-${tone}` : ''}`}>
                <span className="brief__stat-label">{label}</span>
                <span className="brief__stat-value">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- Watch */

export function WatchScreen() {
  return (
    <div className="scr scr-stage scr-watch">
      <div className="watch">
        <span className="watch__btn watch__btn--a" />
        <span className="watch__btn watch__btn--b" />
        <span className="watch__btn watch__btn--c" />
        <div className="watch__bezel">
          <div className="watch__face">
            <svg className="watch__ring" viewBox="0 0 100 100" aria-hidden="true">
              <circle cx="50" cy="50" r="47" fill="none" stroke="rgba(255,255,255,.08)" strokeWidth="2.2" />
              <circle
                cx="50"
                cy="50"
                r="47"
                fill="none"
                stroke="#3ddc84"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeDasharray="230 295.3"
                transform="rotate(130 50 50)"
              />
            </svg>
            <div className="watch__meta mono">METAR · 4 MIN AGO</div>
            <div className="watch__station">KSFO</div>
            <Cat kind="vfr" />
            <div className="watch__wind">
              <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden="true">
                <circle cx="22" cy="22" r="19" fill="none" stroke="rgba(255,255,255,.25)" strokeWidth="1.5" />
                <g transform="rotate(100 22 22)">
                  <path d="M22 6v24M22 34l-5-8h10Z" fill="#4cd3f5" stroke="#4cd3f5" strokeWidth="2.2" strokeLinejoin="round" />
                </g>
              </svg>
              <span>
                280° <small>12KT</small>
              </span>
            </div>
            <div className="watch__row mono">
              <span>10SM</span>
              <span>FEW015</span>
              <span>18/12</span>
            </div>
            <div className="watch__alt mono">A30.02</div>
          </div>
        </div>
      </div>
      <div className="scr-float scr-float--a">
        <span className="scr-dot scr-dot--ok" /> Synced from phone · 2 min ago
      </div>
      <div className="scr-float scr-float--b">
        <span className="mono">NRST</span> KSQL · 7 NM · 128°
      </div>
      <div className="scr-float scr-float--c">
        <span className="scr-dot scr-dot--warn" /> KSFO: MVFR expected after 21Z
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- Ops */

const FLIGHTS = [
  ['AQ101', 'N250AQ', 'C25C', 'KSFO', 'KLAS', '19:30Z', 'Reyes · Kim', 'Boarding', 'cyan'],
  ['AQ214', 'N318AQ', 'CL35', 'KTEB', 'KPBI', '20:05Z', 'Novak · Sato', 'On time', 'ok'],
  ['AQ330', 'N77AQ', 'PC24', 'KDAL', 'KASE', '20:40Z', 'Okafor · Lind', 'Wx hold', 'warn'],
  ['AQ402', 'N902AQ', 'E55P', 'KVNY', 'KSUN', '21:15Z', 'Haddad · Ruiz', 'Fueling', 'cyan'],
  ['AQ517', 'N455AQ', 'C68A', 'KBED', 'KMVY', '21:50Z', 'Brooks · Chen', 'On time', 'ok'],
  ['AQ618', 'N623AQ', 'GLF6', 'KHPN', 'EGGW', '23:00Z', 'Moreau · Patel', 'Scheduled', 'idle'],
];

export function OpsScreen() {
  return (
    <div className="scr scr-ops">
      <StatusBar />
      <header className="ops__bar">
        <div>
          <div className="brief__eyebrow mono">Operations · Wed, Oct 1</div>
          <div className="ops__title">Today’s flights</div>
        </div>
        <div className="brief__tabs">
          <span className="is-active">Today</span>
          <span>Tomorrow</span>
          <span>Week</span>
        </div>
        <span className="ops__new">+ New flight</span>
      </header>
      <div className="ops__body">
        <div className="scr-card ops__table">
          <div className="ops__row ops__row--head">
            <span>Flight</span>
            <span>Aircraft</span>
            <span>Route</span>
            <span>ETD</span>
            <span>Crew</span>
            <span>Status</span>
          </div>
          {FLIGHTS.map(([id, tail, type, from, to, etd, crew, status, tone]) => (
            <div key={id} className="ops__row">
              <span className="mono ops__id">{id}</span>
              <span>
                <b>{tail}</b> <em className="mono">{type}</em>
              </span>
              <span className="mono">
                {from} <i>→</i> {to}
              </span>
              <span className="mono">{etd}</span>
              <span className="ops__crew">{crew}</span>
              <span>
                <span className={`scr-chip scr-chip--${tone}`}>{status}</span>
              </span>
            </div>
          ))}
        </div>
        <aside className="ops__side">
          <div className="scr-card ops__fleet">
            <div className="ops__side-title">Fleet</div>
            {[
              ['Available', 9, 64, 'ok'],
              ['In flight', 4, 29, 'cyan'],
              ['Maintenance', 1, 7, 'warn'],
            ].map(([label, n, pct, tone]) => (
              <div key={label} className="ops__meter">
                <div className="ops__meter-top">
                  <span>{label}</span>
                  <b className="mono">{n}</b>
                </div>
                <span className={`ops__meter-bar is-${tone}`}>
                  <i style={{ width: `${pct}%` }} />
                </span>
              </div>
            ))}
          </div>
          <div className="scr-card ops__alerts">
            <div className="ops__side-title">Alerts</div>
            <div className="ops__alert">
              <span className="scr-dot scr-dot--warn" />
              <div>
                <b>KASE IFR until 21Z</b>
                <span>AQ330 holding for weather</span>
              </div>
            </div>
            <div className="ops__alert">
              <span className="scr-dot scr-dot--cyan" />
              <div>
                <b>N455AQ inspection</b>
                <span>Due in 12 flight hours</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- API */

const J = ({ k, children }) => <span className={`tok-${k}`}>{children}</span>;

const CODE = [
  [<J k="c">{'# Decoded weather for any station, in one call'}</J>],
  [<J k="f">curl</J>, ' ', <J k="s">https://api.yourapp.com/v1/metar/KSFO</J>],
  [''],
  ['{'],
  ['  ', <J k="k">"station"</J>, ': ', <J k="s">"KSFO"</J>, ','],
  ['  ', <J k="k">"observed"</J>, ': ', <J k="s">"2026-10-01T18:56:00Z"</J>, ','],
  ['  ', <J k="k">"flight_category"</J>, ': ', <J k="s">"VFR"</J>, ','],
  ['  ', <J k="k">"wind"</J>, ': { ', <J k="k">"dir"</J>, ': ', <J k="n">280</J>, ', ', <J k="k">"speed_kt"</J>, ': ', <J k="n">12</J>, ' },'],
  ['  ', <J k="k">"visibility_sm"</J>, ': ', <J k="n">10</J>, ','],
  ['  ', <J k="k">"clouds"</J>, ': [{ ', <J k="k">"cover"</J>, ': ', <J k="s">"FEW"</J>, ', ', <J k="k">"base_ft"</J>, ': ', <J k="n">1500</J>, ' }],'],
  ['  ', <J k="k">"temp_c"</J>, ': ', <J k="n">18</J>, ', ', <J k="k">"dewpoint_c"</J>, ': ', <J k="n">12</J>, ','],
  ['  ', <J k="k">"altimeter_inhg"</J>, ': ', <J k="n">30.02</J>, ','],
  ['  ', <J k="k">"raw"</J>, ': ', <J k="s">"KSFO 011856Z 28012KT 10SM FEW015 18/12 A3002"</J>],
  ['}'],
];

const FEEDS = [
  ['METAR', '12 s ago'],
  ['TAF', '4 min ago'],
  ['NOTAM', '38 s ago'],
  ['ADS-B', '1.2 s ago'],
  ['Nav data', 'AIRAC 2610'],
];

export function ApiScreen() {
  return (
    <div className="scr scr-api">
      <div className="api__chrome">
        <span className="api__lights" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
        <span className="api__url mono">api.yourapp.com/v1/metar/KSFO</span>
        <span className="scr-chip scr-chip--ok mono">200 OK · 31 ms</span>
      </div>
      <div className="api__body">
        <div className="api__code mono">
          <div className="api__tabs">
            <span className="is-active">request.sh</span>
            <span>response.json</span>
          </div>
          <ol>
            {CODE.map((parts, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={i}>
                {parts.map((p, j) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <Fragment key={j}>{p}</Fragment>
                ))}
              </li>
            ))}
          </ol>
        </div>
        <aside className="api__side">
          <div className="scr-card api__metric">
            <span className="brief__stat-label">Requests · 24h</span>
            <span className="api__big">2.41M</span>
            <svg viewBox="0 0 260 60" className="api__spark" aria-hidden="true">
              <defs>
                <linearGradient id="api-fill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" style={{ stopColor: 'var(--accent)', stopOpacity: 0.35 }} />
                  <stop offset="1" style={{ stopColor: 'var(--accent)', stopOpacity: 0 }} />
                </linearGradient>
              </defs>
              <path
                d="M0 48 L20 44 L40 46 L60 38 L80 40 L100 30 L120 34 L140 24 L160 28 L180 18 L200 22 L220 12 L240 16 L260 8 V60 H0Z"
                fill="url(#api-fill)"
              />
              <path
                d="M0 48 L20 44 L40 46 L60 38 L80 40 L100 30 L120 34 L140 24 L160 28 L180 18 L200 22 L220 12 L240 16 L260 8"
                fill="none"
                style={{ stroke: 'var(--accent)' }}
                strokeWidth="2.2"
              />
            </svg>
          </div>
          <div className="scr-card api__metric">
            <span className="brief__stat-label">p95 latency</span>
            <span className="api__big">38 ms</span>
            <span className="api__bars" aria-hidden="true">
              {[42, 38, 51, 36, 40, 34, 39, 37, 33, 38, 35, 38].map((v, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <i key={i} style={{ height: `${v}%` }} />
              ))}
            </span>
          </div>
          <div className="scr-card api__feeds">
            <span className="brief__stat-label">Feeds</span>
            {FEEDS.map(([name, age]) => (
              <div key={name} className="api__feed">
                <span className="scr-dot scr-dot--ok" />
                <span>{name}</span>
                <span className="mono">{age}</span>
              </div>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
